import React, { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { Aircraft, Emptyleg } from '@/API/models';
import ReactQueryProvider from '@/API/ReactQueryProvider';
import Modal from '@/components/Modal';
import AddEmptyLegForm from '@/components/Operator/AddEmptyLegForm';
import AircraftCard from '@/components/Operator/AircraftCard';
import EditEmptyLegForm from '@/components/Operator/EditEmptyLegForm';
import EmptyLegsList from '@/components/Operator/EmptyLegsList';
import useMediaQuery from '@/Utils/useMediaQuery';

type Props = {
  aircrafts: Aircraft[];
};

const OperatorFleetList: React.FC<Props> = ({ aircrafts }) => {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [selected, setSelected] = useState<Aircraft | null>(null);
  const [selectedEmptyLeg, setSelectedEmptyLeg] = useState<Emptyleg>();
  const [modalType, setModalType] = useState<'view' | 'edit' | 'add'>();
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const [contentRect, setContentRect] = useState<{ left: number; width: number }>();

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(undefined), 3000);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 3000);
    return () => clearTimeout(t);
  }, [error]);

  // Removed scroll-to-top effect; overlay flash now ensures visibility

  useEffect(() => {
    const updateRect = () => {
      const el = document.getElementById('operator-fleet-content');
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setContentRect({ left: rect.left, width: rect.width });
    };
    updateRect();
    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, []);

  // used when admin acting as operator
  const operatorId = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('operator_id') || undefined;
  }, []);

  const addEmptyLeg = (ac: Aircraft) => {
    if (!ac.emptyleg_type) {
      setError(
        'Please complete your aircraft profile before submitting an one-way special.',
      );
      return;
    } else {
      setSelected(ac);
      setModalType('add');
    }
  };
  const viewEmptyLeg = (ac: Aircraft) => {
    setSelected(ac);
    setModalType('view');
  };
  const editEmptyLeg = (el: Emptyleg) => {
    setSelectedEmptyLeg(el);
    setModalType('edit');
  };
  const closeModal = (msg?: string) => {
    msg && setMessage(msg);
    setError('');
    setSelected(null);
  };

  return (
    <ReactQueryProvider>
      <Modal
        style={{
          content: {
            maxHeight: '90vh',
            overflow: modalType === 'view' ? 'auto' : 'visible',
            maxWidth: modalType === 'add' && !isMobile ? '50%' : undefined,
          },
        }}
        isOpen={!!selected}
        onRequestClose={() => closeModal()}
        shouldCloseOnOverlayClick={!(modalType === 'add')}
        className={'operator-dashboard-modal'}
      >
        {selected &&
          (modalType === 'add' ? (
            <AddEmptyLegForm
              aircraft={selected}
              afterSubmit={() =>
                closeModal('One-Way Special added successfully')
              }
              onError={(errMsg) => {
                setSelected(null);
                setTimeout(() => {
                  setMessage(undefined);
                  setError(errMsg);
                }, 0);
              }}
            />
          ) : modalType === 'edit' ? (
            selectedEmptyLeg && (
              <EditEmptyLegForm
                aircraft={selected}
                emptyleg={selectedEmptyLeg}
                afterSubmit={() =>
                  closeModal('One-Way Special modifed successfully')
                }
              />
            )
          ) : (
            <EmptyLegsList aircraft={selected} onEdit={editEmptyLeg} />
          ))}
      </Modal>
      {(message || error) &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className='fixed'
            style={{
              zIndex: 2147483647,
              pointerEvents: 'none',
              left: (contentRect?.left || 0) - 30,
              width: contentRect?.width ? Math.max(contentRect.width - 32, 0) : 'calc(100% - 32px)',
              top: '10%'
            }}
          >
            {message && (
              <div className='w-full p-10 text-left bg-stripe-green-light text-gray-600'>
                {message}
              </div>
            )}
            {error && (
              <div className='w-full p-10 text-left bg-red-100 text-gray-600'>
                {error}
              </div>
            )}
          </div>,
          document.body,
        )}
      <div id='operator-fleet-content' className='grid gap-8 md:gap-16 clear-both custom-xl:grid-cols-rep-2-35 grid-cols-rep-1-full'>
        {aircrafts.map((aircraft, i) => (
          <AircraftCard
            operatorId={operatorId}
            aircraft={aircraft}
            add={addEmptyLeg}
            view={viewEmptyLeg}
            key={i}
          />
        ))}
      </div>
    </ReactQueryProvider>
  );
};
export default OperatorFleetList;